import { Globe, ChevronRight } from 'lucide-react';
import { REGION, REGION_SELECTOR_URL } from '@/constants/region';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ComponentPropsWithoutRef } from 'react';

type TooltipSide = ComponentPropsWithoutRef<typeof TooltipContent>['side'];

interface RegionBadgeProps {
  tooltipSide?: TooltipSide;
  className?: string;
  /** `rail` matches the taskbar: a 36px square showing the region code. */
  variant?: 'inline' | 'rail';
}

const RAIL_CLASSNAME =
  'flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-xs font-semibold text-foreground shadow-sm transition-colors';

export function RegionBadge({
  tooltipSide = 'bottom',
  className,
  variant = 'inline',
}: RegionBadgeProps) {
  if (!REGION) return null;

  const tooltipText = REGION_SELECTOR_URL
    ? `${REGION} — switch region`
    : `Region: ${REGION}`;

  if (variant === 'rail') {
    const railTrigger = REGION_SELECTOR_URL ? (
      <a
        href={REGION_SELECTOR_URL}
        target='_blank'
        rel='noopener noreferrer'
        aria-label={`Region: ${REGION}. Click to switch region.`}
        className={`${RAIL_CLASSNAME} hover:bg-accent hover:text-accent-foreground`}
      >
        {REGION}
      </a>
    ) : (
      <div className={RAIL_CLASSNAME} aria-label={`Region: ${REGION}`}>
        {REGION}
      </div>
    );

    return (
      // Centred rather than left-aligned: the rail is wider than the button in
      // the labelled mode, where the two only line up by coincidence at 52px.
      <div className={cn('flex justify-center', className)}>
        <Tooltip>
          <TooltipTrigger asChild>{railTrigger}</TooltipTrigger>
          <TooltipContent side={tooltipSide}>{tooltipText}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  const trigger = REGION_SELECTOR_URL ? (
    <a
      href={REGION_SELECTOR_URL}
      target='_blank'
      rel='noopener noreferrer'
      aria-label={`Region: ${REGION}. Click to switch region.`}
      className='flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors'
    >
      <Globe className='size-3' />
      <span className='group-data-[collapsible=icon]:hidden'>{REGION}</span>
      <ChevronRight className='size-3 opacity-50 group-data-[collapsible=icon]:hidden' />
    </a>
  ) : (
    <div
      className='flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground transition-colors'
      aria-label={`Region: ${REGION}`}
    >
      <Globe className='size-3' />
      <span className='group-data-[collapsible=icon]:hidden'>{REGION}</span>
    </div>
  );

  return (
    <div className={className}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side={tooltipSide}>{tooltipText}</TooltipContent>
      </Tooltip>
    </div>
  );
}
